import React, { useEffect, useState } from 'react';
import {
  ScatterChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  ZAxis,
  Scatter,
  Tooltip,
  CartesianGrid,
  Cell,
  ReferenceArea,
  Label,
} from 'recharts';
import { useData } from '../../contexts';

export const ScatterChartHighlightAndZoom = (props: any) => {
  const {
    pageId,
    xAxisDataKey,
    yAxisDataKey,
    zAxisDataKey,
    xTooltip,
    referenceAreas,
    labelGraph,
    incomingData,
    zoomGraph,
    setZoomGraph,
    setIsZoomedInsights,
    setDataTable,
    zoomFilters,
    setZoomFilters,
    selectedIndicatorsInzichten,
    locale,
    canZoom,
    highlightedRow,
    setHighlightedRow,
    isMobile,
    isBench,
    tableHighlightedRow,
    setTableHighlightedRow,
  } = props;

  const { fillColorHighlight } = useData();

  const [dataGraph, setDataGraph] = useState(incomingData) as any[];
  const zoom = () => {
    let { refAreaLeft, refAreaRight } = zoomGraph;

    const data = structuredClone(incomingData);

    if (refAreaLeft === refAreaRight || refAreaRight === '') {
      setZoomGraph((prev: any) => ({
        ...prev,
        refAreaLeft: '',
        refAreaRight: '',
      }));
      return;
    }

    // xAxis domain
    if (refAreaLeft && refAreaRight && refAreaLeft > refAreaRight)
      [refAreaLeft, refAreaRight] = [refAreaRight, refAreaLeft];

    // const [bottom, top] = [-1, 1];

    const newTableData = data.filter((d: any) => {
      return d[xAxisDataKey] >= refAreaLeft && d[xAxisDataKey] <= refAreaRight;
    });

    if (newTableData.length === 0) return;

    setIsZoomedInsights(true);

    const unique = [...new Set(newTableData.map((d: any) => d.id))];

    setZoomFilters((prev: any) => ({ ...prev, [pageId]: unique }));

    setZoomGraph(
      (prev: any) =>
        ({
          ...prev,
          refAreaLeft: refAreaLeft,
          refAreaRight: refAreaRight,
          data: data?.slice(),
          left: refAreaLeft,
          right: refAreaRight,
          bottom,
          top,
        } as any),
    );
  };

  const { left, right, refAreaLeft, refAreaRight, top, bottom } = zoomGraph;

  useEffect(() => {
    let filteredData = structuredClone(incomingData);

    for (const property in zoomFilters) {
      filteredData = filteredData.filter((d: any) => {
        return zoomFilters[property].includes(d.id);
      });
    }

    const theRow = filteredData.find((d: any) => d.id === highlightedRow);
    const dataWithoutTheRow = filteredData.filter(
      (d: any) => d.id !== highlightedRow,
    );

    const visualData =
      theRow !== undefined
        ? dataWithoutTheRow.concat([theRow])
        : dataWithoutTheRow;

    setZoomGraph(
      (prev: any) =>
        ({
          ...prev,
          refAreaLeft: '',
          refAreaRight: '',
          data: filteredData,
          left: refAreaLeft,
          right: refAreaRight,
          bottom,
          top,
        } as any),
    );

    // const visData = visualData.filter((d: any) => {
    //   return d[xAxisDataKey] !== null && parseInt(d[xAxisDataKey]) !== 0;
    // });

    setDataGraph(visualData);
    setDataTable(filteredData);
    // }, [zoomFilters, highlightedRow]);
  }, [zoomFilters]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      if (payload[0].value === null || parseInt(payload[0].value) === 0) {
        return;
      }
      setHighlightedRow(null);
      const filteredData = dataGraph.filter((d: any) => {
        return d[xAxisDataKey] === payload[0].value;
      });

      return (
        <div
          style={{
            backgroundColor: 'white',
            padding: 8,
            border: '1px solid #aaa',
            maxWidth: '400px',
          }}
        >
          <p>
            Gestandaardiseerde positie:{' '}
            {isBench
              ? filteredData[0].rankNormalisedBench
              : filteredData[0].rankNormalised}
            /100
          </p>

          {filteredData.map((d: any) => {
            return (
              <ul>
                <li>
                  {d.indicator} <br />({isBench ? d.rankBench : d.rank}/
                  {d.rankTotaal})
                </li>
              </ul>
            );
          })}
        </div>
      );
    }
  };

  const splitLines = (text: String) => {
    var chunks = text.match(/\S.{1,34}(?=\s|$)/g);
    return chunks;
  };

  const CustomYAxisLabel = ({ x, y, value }) => {
    const chunks = splitLines(value);
    let yPos = 30;
    if (chunks.length === 2) {
      yPos = 22;
    }
    if (chunks.length === 3) {
      yPos = 18;
    }

    return (
      <text
        width="100%"
        x={0}
        y={0}
        dy={0}
        textAnchor="end"
        fill="#666"
        fontSize="16px"
      >
        {chunks.map((d, index) => (
          <tspan x={260} dy={`${index === 0 ? yPos : 18}`} key={index}>
            {d + ' '}
          </tspan>
        ))}
      </text>
    );
  };

  const handleMouseEnter = (event) => {
    // Access the data point ID from the event object

    const id = event.target.id;
    setTableHighlightedRow(id);
  };

  const handleMouseOut = (event) => {
    setHighlightedRow(null);
    // setTableHighlightedRow(null);
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ScatterChart
        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
        data={dataGraph}
        onMouseDown={(e) => {
          if (canZoom) {
            setZoomGraph((prev: any) => ({
              ...prev,
              refAreaLeft: e !== null && e.xValue,
            }));
          }
        }}
        onMouseMove={(e) => {
          if (canZoom) {
            zoomGraph.refAreaLeft &&
              setZoomGraph((prev: any) => ({
                ...prev,
                refAreaRight: e !== null && e.xValue,
              }));
          }
        }}
        onMouseUp={() => {
          if (canZoom) {
            zoom();
          }
        }}
        onMouseLeave={(e: any) => {
          setZoomGraph((prev: any) => ({
            ...prev,
            refAreaLeft: null,
            refAreaRight: null,
          }));
        }}
      >
        {/* <CartesianGrid strokeDasharray="3 3" /> */}
        <XAxis
          allowDataOverflow={true}
          dataKey={xAxisDataKey}
          // domain={left && right ? [left, right] : [-4, 104]}
          domain={[-4, 104]}
          type="number"
          tickCount={6}
          tickFormatter={(d, i) => {
            return incomingData[0] && incomingData[0].d3format
              ? locale.format(incomingData[0].d3format)(d)
              : d;
          }}
          tickMargin={12}
          interval={0}
          tick={false}
          tickLine={false}
          axisLine={false}
        >
          {isBench && !isMobile && (
            <>
              <Label
                value="Hoogste in rangschikking"
                offset={0}
                position="insideTopLeft"
                style={{ textAnchor: 'start', fill: '#888' }}
              />
              <Label
                value="(Hoogste waarden)"
                offset={16}
                position="insideTopLeft"
                style={{ textAnchor: 'start', fill: '#aaa', fontSize: '12px' }}
              />
              <Label
                value="Laagste in rangschikking"
                offset={0}
                position="insideTopRight"
                style={{
                  textAnchor: 'end',
                  fill: '#888',
                }}
              />
              <Label
                value="(Laagste waarden)"
                offset={16}
                position="insideTopRight"
                style={{ textAnchor: 'end', fill: '#aaa', fontSize: '12px' }}
              />
            </>
          )}
        </XAxis>
        <YAxis
          allowDataOverflow
          dataKey={yAxisDataKey}
          domain={[bottom, top]}
          type="number"
          yAxisId="1"
          tick={false}
          tickLine={false}
          axisLine={false}
          height={10}
          width={isMobile ? 40 : 260}
          // label={{
          //   value: isMobile ? '' : labelGraph,
          //   position: 'insideRight',
          // }}
          label={!isMobile && <CustomYAxisLabel value={labelGraph} />}
        />
        <ZAxis range={[0, 500]} dataKey={zAxisDataKey} />

        {referenceAreas.map((d: any, i: number) => {
          return (
            <ReferenceArea
              key={d.id}
              yAxisId="1"
              y1={0.4}
              y2={1.6}
              x1={d.x1}
              x2={d.x2}
              stroke={d.color}
              fill={d.color}
              fillOpacity={1}
              strokeOpacity={1}
              radius={
                i === referenceAreas.length - 1
                  ? [0, 20, 20, 0]
                  : i === 0
                  ? [20, 0, 0, 20]
                  : 0
              }
              ifOverflow="hidden"
              onClick={() => {
                if (canZoom) {
                  alert('implement click quartile to filter');
                }
              }}
            />
          );
        })}

        <Scatter
          style={{ cursor: 'pointer' }}
          yAxisId="1"
          type="natural"
          dataKey={xAxisDataKey}
          stroke={'#999'}
          strokeOpacity={0.4}
          animationDuration={0}
          isAnimationActive={false}
        >
          {dataGraph.map((entry: any, index: number) => {
            return (
              <Cell
                key={`cell-${entry?.id}`}
                fill={
                  entry[xAxisDataKey] === null ||
                  parseInt(entry[xAxisDataKey]) === 0
                    ? 'transparent'
                    : entry?.id === highlightedRow ||
                      parseInt(entry?.id) === parseInt(tableHighlightedRow)
                    ? fillColorHighlight
                    : '#fff'
                }
                fillOpacity={
                  entry[xAxisDataKey] === null ||
                  parseInt(entry[xAxisDataKey]) === 0
                    ? 0
                    : entry?.id === highlightedRow ||
                      parseInt(entry?.id) === parseInt(tableHighlightedRow)
                    ? 1
                    : 0.4
                }
                // stroke={
                //   parseInt(entry?.id) === parseInt(tableHighlightedRow)
                //     ? 'blue'
                //     : 'black'
                // }
                strokeOpacity={
                  entry[xAxisDataKey] === null ||
                  parseInt(entry[xAxisDataKey]) === 0
                    ? 0
                    : 0.6
                }
                onMouseEnter={handleMouseEnter}
                onMouseOut={handleMouseOut}
              />
            );
          })}
        </Scatter>

        <Tooltip
          allowEscapeViewBox={{ x: false, y: true }}
          wrapperStyle={{ zIndex: 10 }}
          content={<CustomTooltip />}
          cursor={{ strokeDasharray: '3 3', stroke: '#666', strokeWidth: 1 }}
        />

        {refAreaLeft && refAreaRight ? (
          <ReferenceArea
            yAxisId="1"
            x1={refAreaLeft}
            x2={refAreaRight}
            strokeOpacity={0.3}
          />
        ) : null}
      </ScatterChart>
    </ResponsiveContainer>
  );
};
